import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { api } from "state/api";
import {
  K_WINNING_HISTORY_CHUNK_SIZE,
  K_WINNING_HISTORY_MAX_FETCH,
  mergeHistoryByRank,
  winningHistoryFetchSize,
} from "utils/prizeDisplay";

const rowKey = (entry) => entry._id || entry.id;

const rowExcludedFromRank = (entry) =>
  entry?.exitOnMid === true || String(entry?.exitOnMid).toLowerCase() === "true";

export function useWinningHistoryPagination({
  productId,
  playToken,
  winningDocs,
  seedHistory = [],
  rankCeiling = 0,
}) {
  const dispatch = useDispatch();
  const [winningHistory, setWinningHistory] = useState([]);
  const [historyByRank, setHistoryByRank] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [fetchComplete, setFetchComplete] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(0);
  const targetRef = useRef(0);
  const inFlightRef = useRef(false);

  const rebuildRankMap = useCallback((history) => {
    setHistoryByRank(mergeHistoryByRank(history));
  }, []);

  const fetchPage = useCallback(
    async (page, pageSize) =>
      dispatch(
        api.endpoints.getPlayTableHistory.initiate(
          { productId, playToken, page, pageSize },
          { forceRefetch: page > 1 }
        )
      ).unwrap(),
    [dispatch, productId, playToken]
  );

  const mergePageHistory = useCallback((prev, pageHistory) => {
    const existingIds = new Set(prev.map(rowKey));
    const merged = [...prev];
    for (const entry of pageHistory) {
      const key = rowKey(entry);
      if (!existingIds.has(key)) {
        merged.push(entry);
        existingIds.add(key);
      }
    }
    return merged;
  }, []);

  const seedFromHistory = useCallback((seed) => {
    const history = [];
    const existingIds = new Set();
    for (const entry of seed) {
      const key = rowKey(entry);
      if (entry.rank > 0 && !rowExcludedFromRank(entry) && !existingIds.has(key)) {
        history.push(entry);
        existingIds.add(key);
      }
    }
    return history;
  }, []);

  const reset = useCallback(() => {
    setWinningHistory([]);
    setHistoryByRank({});
    setLoading(false);
    setLoadingMore(false);
    setLoaded(false);
    setFetchComplete(false);
    setHasMore(false);
    pageRef.current = 0;
    targetRef.current = 0;
    inFlightRef.current = false;
  }, []);

  const loadInitial = useCallback(async () => {
    if (!productId || !playToken || inFlightRef.current) return false;

    const targetCount = winningHistoryFetchSize(winningDocs, rankCeiling);
    targetRef.current = targetCount;
    inFlightRef.current = true;
    setLoading(true);
    setFetchComplete(false);
    setHasMore(false);
    pageRef.current = 0;

    let history = seedFromHistory(seedHistory);
    setWinningHistory(history);
    rebuildRankMap(history);

    if (targetCount <= 0) {
      setLoaded(true);
      setFetchComplete(true);
      setLoading(false);
      inFlightRef.current = false;
      return true;
    }

    if (history.length >= targetCount) {
      setLoaded(true);
      setFetchComplete(true);
      setLoading(false);
      inFlightRef.current = false;
      return true;
    }

    try {
      let page = 1;
      let pageHasMore = true;
      const firstFetchSize =
        targetCount > K_WINNING_HISTORY_MAX_FETCH
          ? K_WINNING_HISTORY_MAX_FETCH
          : targetCount;

      while (history.length < targetCount && pageHasMore) {
        const remaining = targetCount - history.length;
        const pageSize =
          page === 1
            ? firstFetchSize
            : Math.min(K_WINNING_HISTORY_CHUNK_SIZE, remaining);

        const response = await fetchPage(page, pageSize);
        if (
          response.message === "No matching play table history found" ||
          !response.history?.length
        ) {
          pageHasMore = false;
          break;
        }

        history = mergePageHistory(history, response.history);
        setWinningHistory(history);
        rebuildRankMap(history);
        pageRef.current = page;
        pageHasMore = Boolean(response.hasMore);

        if (!pageHasMore || history.length >= targetCount) break;
        page += 1;
      }

      const complete = history.length >= targetCount || !pageHasMore;
      setFetchComplete(complete);
      setHasMore(!complete && pageHasMore);
      setLoaded(true);
      return true;
    } catch {
      setLoaded(history.length > 0);
      return history.length > 0;
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [
    productId,
    playToken,
    winningDocs,
    rankCeiling,
    seedHistory,
    fetchPage,
    mergePageHistory,
    rebuildRankMap,
    seedFromHistory,
  ]);

  const loadMore = useCallback(async () => {
    if (
      inFlightRef.current ||
      loading ||
      loadingMore ||
      fetchComplete ||
      !hasMore
    ) {
      return false;
    }
    if (!productId || !playToken) return false;

    inFlightRef.current = true;
    setLoadingMore(true);

    try {
      let history = winningHistory;
      let page = pageRef.current + 1;
      const targetCount = targetRef.current;
      let pageHasMore = true;

      while (history.length < targetCount && pageHasMore) {
        const remaining = targetCount - history.length;
        const pageSize = Math.min(K_WINNING_HISTORY_CHUNK_SIZE, remaining);
        const response = await fetchPage(page, pageSize);

        if (
          response.message === "No matching play table history found" ||
          !response.history?.length
        ) {
          pageHasMore = false;
          break;
        }

        history = mergePageHistory(history, response.history);
        setWinningHistory(history);
        rebuildRankMap(history);
        pageRef.current = page;
        pageHasMore = Boolean(response.hasMore);

        if (!pageHasMore || history.length >= targetCount) break;
        page += 1;
      }

      const complete = history.length >= targetCount || !pageHasMore;
      setFetchComplete(complete);
      setHasMore(!complete && pageHasMore);
      return true;
    } catch {
      return false;
    } finally {
      setLoadingMore(false);
      inFlightRef.current = false;
    }
  }, [
    productId,
    playToken,
    winningHistory,
    fetchComplete,
    hasMore,
    loading,
    loadingMore,
    fetchPage,
    mergePageHistory,
    rebuildRankMap,
  ]);

  return {
    winningHistory,
    historyByRank,
    loading,
    loadingMore,
    loaded,
    fetchComplete,
    hasMore,
    loadInitial,
    loadMore,
    reset,
  };
}

export default useWinningHistoryPagination;
